import {
  CustomerBillingModel,
  CustomerLiteModel,
  CustomerModel,
  getCustomerKey,
  getCustomersKey,
  useGetCustomer,
  useGetCustomers,
} from 'api-hooks/customer';
import { Caption } from 'baseui/form-control/styled-components';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { string2money, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import BillingAccountTooltipList from 'legacy-modules/master/common/billing-account-list';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: CustomerLiteModel | CustomerModel,
  t: Translate,
): Option {
  const name = transformLabel(
    [item.title && t(`enum:${item.title}`), item.name],
    ' ',
  );
  return {
    id: item.id,
    label: transformLabel([name, item.alias]) + ` (${item.phone})`,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: CustomerModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
  isShowBalance?: boolean;
  isShowAccountInfo?: boolean;
  isShowTransactionQuota?: boolean;
}

export default function CustomerSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:customer'),
    placeholder = t('common:select_customer'),
    isShowBalance = false,
    isShowAccountInfo,
    isShowTransactionQuota = false,
  } = props;
  const { editable } = useFormState();
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const [balanceText, setBalanceText] = React.useState<number>(0);
  const [billingAccounts, setBillingAccounts] = React.useState<
    CustomerBillingModel[]
  >([]);
  const [remainingTransactionQuotaText, setRemainingTransactionQuotaText] =
    React.useState<number | undefined>(undefined);

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('customers');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: React.useCallback(
      (value) => {
        setValue(props.name, value[0]?.id, {
          shouldTouch: true,
          shouldValidate: true,
        });
      },
      [props.name, setValue],
    ),
    useListQueryHook: useGetCustomers,
    useDetailLazyQueryHook: useGetCustomer,
    listKeyFunction: getCustomersKey,
    detailKeyFunction: getCustomerKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    renderCreate: permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.CustomersCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback(
      (result) => {
        return result.data.map((v) => transformer(v, t));
      },
      [t],
    ),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, CustomerModel);
        props.onAfterDetailChange?.(newResult.data);
        setBalanceText((newResult.data as CustomerModel).balance);
        isShowAccountInfo &&
          setBillingAccounts((newResult.data as CustomerModel).billings);
        setRemainingTransactionQuotaText(
          (newResult.data as CustomerModel).system.remainingTransactionQuota,
        );

        return transformer(newResult.data, t);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isShowAccountInfo, props.onAfterDetailChange],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      filterOptions={null}
      overrides={overrides}
      caption={
        <>
          {isShowBalance && (
            <Caption>
              {t('common:balance_extra', {
                extra: string2money(balanceText),
              })}
            </Caption>
          )}
          {isShowAccountInfo && (
            <BillingAccountTooltipList data={billingAccounts} />
          )}
          {isShowTransactionQuota &&
            remainingTransactionQuotaText &&
            editable && (
              <Caption>
                {t('common:remaining_transaction_quota', {
                  extra: string2money(remainingTransactionQuotaText),
                })}
              </Caption>
            )}
        </>
      }
      {...selectProps}
    />
  );
}
